// Sentry MUST come first so the SDK is loaded before any other module
// that might throw at evaluation time. The `Sentry.init()` call below runs
// immediately after the import block, so capture starts as early as the
// browser hands us control.
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import '@fontsource/nunito-sans/800.css';
import '@fontsource/nunito-sans'; // Defaults to weight 400
import '@fontsource/nunito-sans/700.css'; // weight 700
import '@fontsource/nunito-sans/600.css'; // weight 800
import './core/utils/dayjsSetup';
import constants from './core/constants';
import Routes from './core/routes';
import DebugWrapper from './core/utils/debug';
import { dropHandledRejections } from './core/utils/sentryFilters';
import NotificationPoller from './core/notification/NotificationPoller';
import { setVersion } from './core/global/globalSlice';
import { resetAuthTransientStates } from './core/user/userSlice';
import './styles/style.scss';
/** configureStore */
import { store, persistor } from './core/store';
/** The version of the app but not displayed yet */
import pack from '../package.json';

if (constants.SENTRY_DSN) {
    Sentry.init({
        dsn: constants.SENTRY_DSN,
        environment: constants.ENV,
        release: constants.VERSION,
        // Send default PII (e.g. IP addresses) so errors are easier to debug.
        sendDefaultPii: true,
        // A form rule the user was shown and can act on is not an exception.
        beforeSend: dropHandledRejections,
    });
}

const VERSION = pack.version;

const AppInitialiser = () => {
    const dispatch = useDispatch();
    dispatch(setVersion(VERSION));
    // Clean up any persisted authentication states that might cause issues
    dispatch(resetAuthTransientStates());
    return null;
};

const rootElement = document.getElementById('root') as HTMLElement;

const root = createRoot(rootElement);

root.render(
    <DebugWrapper>
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <AppInitialiser />
                <NotificationPoller />
                <BrowserRouter
                    future={{ v7_startTransition: true }}
                    basename="/">
                    <Routes />
                </BrowserRouter>
            </PersistGate>
        </Provider>
    </DebugWrapper>,
);
